import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { registerUiTranslations } from '@silia-mx/qa-assistant-ui';

import * as sharedInbox from '@shared/inbox/src/i18n';
import * as sharedUi from '@shared/ui/src/i18n';
import * as sharedWidget from '@shared/widget/src/i18n';
import * as sharedConfiguration from '@shared/configuration/src/i18n';
import * as organization from './features/organizations/i18n';
import * as qaAgent from './features/qa-agent/i18n';
import * as agentOverview from './features/home/pages/AgentOverviewPage/i18n';
import * as campaigns from './features/campaigns/i18n';
import * as navigation from './features/navigation/i18n';
import * as home from './features/home/i18n';
import * as auth from './features/auth/i18n';
import * as account from './features/account/i18n';
import { LANGUAGE_DETECTION } from './features/language/model/languages';

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .on('languageChanged', () => {
    // `languageChanged`'s own argument is `i18n.language`, the RAW resolved
    // value — with `load: 'languageOnly'`, that stays regional (e.g. 'pt-BR')
    // even though the loaded translations are keyed by the bare 'pt'.
    // `resolvedLanguage` is the bare code that actually matches a resource
    // bundle; that's what `<html lang>` must reflect.
    document.documentElement.lang = i18n.resolvedLanguage ?? i18n.language;
  })
  .init({
    fallbackLng: 'en',
    // Resources are keyed by bare language ('pt'), not by the region variant
    // the browser reports ('pt-BR', 'pt-PT', 'es-MX'…). `load: 'languageOnly'`
    // is required for that to resolve correctly: without it, i18next's
    // resolver scans ALL detected codes for an exact `supportedLngs` match
    // before it ever reduces a regional code to its language part — so a
    // *secondary* browser language that happens to have an exact bare match
    // (e.g. 'es') wins over the primary preference ('pt-BR'), even though
    // 'pt' is supported too. Confirmed on staging (SL-1732 QA round 2).
    load: 'languageOnly',
    supportedLngs: ['en', 'es', 'pt'],
    ns: [
      sharedWidget.NS,
      sharedInbox.NS,
      sharedUi.NS,
      sharedConfiguration.NS,
      organization.NS,
      qaAgent.NS,
      agentOverview.NS,
      campaigns.NS,
      navigation.NS,
      home.NS,
      auth.NS,
      account.NS,
    ],
    defaultNS: sharedInbox.NS,
    // An explicit choice from the LanguageSwitcher wins; without one, the
    // browser/OS language decides on every load (detection is not cached).
    detection: LANGUAGE_DETECTION,
    resources: {
      es: {
        [sharedWidget.NS]: sharedWidget.es,
        [sharedInbox.NS]: sharedInbox.es,
        [sharedUi.NS]: sharedUi.es,
        [sharedConfiguration.NS]: sharedConfiguration.es,
        [organization.NS]: organization.es,
        [qaAgent.NS]: qaAgent.es,
        [agentOverview.NS]: agentOverview.es,
        [campaigns.NS]: campaigns.es,
        [navigation.NS]: navigation.es,
        [home.NS]: home.es,
        [auth.NS]: auth.es,
        [account.NS]: account.es,
      },
      en: {
        [sharedWidget.NS]: sharedWidget.en,
        [sharedInbox.NS]: sharedInbox.en,
        [sharedUi.NS]: sharedUi.en,
        [sharedConfiguration.NS]: sharedConfiguration.en,
        [organization.NS]: organization.en,
        [qaAgent.NS]: qaAgent.en,
        [agentOverview.NS]: agentOverview.en,
        [campaigns.NS]: campaigns.en,
        [navigation.NS]: navigation.en,
        [home.NS]: home.en,
        [auth.NS]: auth.en,
        [account.NS]: account.en,
      },
      // 'campaigns' has no pt.json yet, so that namespace falls back to
      // fallbackLng ('en') for pt users until it gets translated.
      pt: {
        [sharedWidget.NS]: sharedWidget.pt,
        [sharedInbox.NS]: sharedInbox.pt,
        [sharedUi.NS]: sharedUi.pt,
        [sharedConfiguration.NS]: sharedConfiguration.pt,
        [organization.NS]: organization.pt,
        [qaAgent.NS]: qaAgent.pt,
        [agentOverview.NS]: agentOverview.pt,
        [navigation.NS]: navigation.pt,
        [home.NS]: home.pt,
        [auth.NS]: auth.pt,
        [account.NS]: account.pt,
      },
    },
    interpolation: {
      escapeValue: false,
    },
  });

// @silia-mx/qa-assistant-ui doesn't own an i18next instance (see ADR-0009 §2.2) —
// it plugs into the host's, under its own 'ui' namespace (no collision with
// 'shared-ui' above). Covers Upload/KeyMoments/Scorecards/Inbox internal strings.
registerUiTranslations(i18n);

export default i18n;
